/* global store ENVIRONMENT */
/* global IS_PREVIEW_PAGE */

const getElementOffset = (rootElement) => {
  const { revealFormat } = store.getState();
  const rootDocument = rootElement.ownerDocument;
  const rootDocumentElement = rootDocument.documentElement;
  const rootWindow = rootDocument.defaultView;
  const width = rootElement.offsetWidth;
  const height = revealFormat ? rootElement.parentNode.offsetHeight : rootElement.offsetHeight;
  if (!rootElement.getClientRects().length) {
    return {
      top: 0,
      left: 0,
      right: width,
      bottom: height,
    };
  }

  const rect = revealFormat ? rootElement.parentNode.getBoundingClientRect() : rootElement.getBoundingClientRect();
  const top = (rect.top + rootWindow.pageYOffset) - rootDocumentElement.clientTop;
  const left = (rect.left + rootWindow.pageXOffset) - rootDocumentElement.clientLeft;
  return {
    top,
    left,
    right: left + width,
    bottom: top + height,
  };
};

const getWindowViewport = (rootElement) => {
  const rootDocument = rootElement.ownerDocument;
  const rootWindow = rootDocument.defaultView;
  const viewport = {
    top: rootWindow.pageYOffset
      ||
      rootDocument.documentElement.scrollTop
      ||
      rootDocument.body.scrollTop
      ||
      0,
    left: rootWindow.pageXOffset
      ||
      rootDocument.documentElement.scrollLeft
      ||
      rootDocument.body.scrollLeft
      ||
      0,
  };
  viewport.right = viewport.left + rootWindow.innerWidth;
  viewport.bottom = viewport.top + rootWindow.innerHeight;
  return viewport;
};

const getPreviewContainer = () => {
  try {
    const rootDocument = window.top.document;
    const previewElements = ['.preview-device-container', '#ad-preview-container'];
    return rootDocument.querySelector(previewElements);
  } catch (e) {
    return null;
  }
};

const getPreviewViewportElement = () => {
  try {
    const rootDocument = window.top.document;
    const previewViewportElements = ['.preview-scrollable-container'];
    const rootPreviewViewportElements = rootDocument.querySelector(previewViewportElements);
    const nestedPreviewVewportElements = frameElement ? frameElement.closest(previewViewportElements) : null;
    return rootPreviewViewportElements || nestedPreviewVewportElements;
  } catch (e) {
    return null;
  }
};

const getPreviewViewport = () => {
  const previewContainer = window.top.document ? getPreviewViewportElement() : null;
  const previewContainerBoundingBox = previewContainer.getBoundingClientRect();
  return {
    top: previewContainerBoundingBox.top - 40,
    left: previewContainerBoundingBox.left,
    bottom: previewContainerBoundingBox.bottom - 40,
    right: previewContainerBoundingBox.right,
  };
};

const isDocumentHidden = (rootElement) => {
  const rootDocument = rootElement.ownerDocument;
  if (typeof rootDocument.msHidden !== 'undefined') {
    return rootDocument.msHidden;
  } else if (typeof rootDocument.webkitHidden !== 'undefined') {
    return rootDocument.webkitHidden;
  } else if (typeof rootDocument.hidden !== 'undefined') {
    return rootDocument.hidden;
  } else if (typeof rootDocument.mozHidden !== 'undefined') {
    return rootDocument.mozHidden;
  } else if (typeof rootDocument.visibilityState !== 'undefined') {
    return rootDocument.visibilityState === 'hidden';
  }
  return false;
};

const getBaseWindowHeight = () => {
  try {
    return window.top.innerHeight;
  } catch (e) {
    return window.innerHeight;
  }
}

export default () => {
  if (ENVIRONMENT === 'pugpig') { // eslint-disable-line no-undef
    return 100;
  }

  const { rootElement, scrollElement, revealFormat, mraid, safeFrame, visiblePercentage, scrollerScreenHeight = null } = store.getState();
  if (mraid && mraid.getVersion() === '3.0') {
    if (mraid.isViewable) return mraid.isViewable() ? 100 : 0;
    return visiblePercentage;
  } else if (mraid) {
    return mraid.isViewable() ? 100 : 0;
  } else if (safeFrame) {
    return safeFrame.inViewPercentage();
  }

  const scrollElementHeight = revealFormat ? scrollElement.parentNode.offsetHeight : scrollElement.offsetHeight;
  const offsetHeight = scrollElementHeight || 0;
  const offsetWidth = scrollElement.offsetWidth || 0;

  if (!offsetWidth || !offsetHeight) {
    return 0;
  }

  const isResponsivePreview = (IS_PREVIEW_PAGE && !frameElement);
  const previewConatiner = frameElement ? getPreviewContainer() : null;
  const visibilityElement = isResponsivePreview ? scrollElement.parentNode.parentNode : scrollElement;
  const viewport = getPreviewViewportElement() ? getPreviewViewport() : getWindowViewport(visibilityElement);
  const offset = getElementOffset(visibilityElement);
  const baseWindowHeight = getBaseWindowHeight();

  if (revealFormat) {
    const top = (offset.top - viewport.top);
    const height = visibilityElement.offsetHeight;
    const viewportHeight = visibilityElement.parentNode.clientHeight;
    const windowHeight = previewConatiner ? previewConatiner.offsetHeight : scrollerScreenHeight || baseWindowHeight;
    const heightDifference = windowHeight - height;
    const viewportHeightDifference = windowHeight - viewportHeight;

    let topPercentage = top / windowHeight;
    if (topPercentage > 1) topPercentage = 1;
    if (topPercentage < 0) topPercentage = 0;

    const adjustablePercentage = (viewportHeightDifference !== 0) ? top / viewportHeightDifference : 0;
    let calcTop = heightDifference * adjustablePercentage;

    const maskRect = visibilityElement.parentNode.getBoundingClientRect();

    if (heightDifference < 0) {
      calcTop = (heightDifference * topPercentage);
    } else {
      if (calcTop > heightDifference) calcTop = heightDifference;
      if (calcTop < 0) calcTop = 0;
    }

    visibilityElement.style.top = `${calcTop}px`;

    [...rootElement.querySelectorAll('div[data-pinned=true]')].forEach((e) => {
      const wrapperTop = visibilityElement.getBoundingClientRect().top; // eslint-disable-line no-undef
      e.style.marginTop = `${maskRect.top - parseFloat(wrapperTop)}px`;
    });
  }

  const visible = !(
    viewport.right < offset.left
    ||
    viewport.left > offset.right
    ||
    viewport.bottom < offset.top
    ||
    viewport.top > offset.bottom
  );

  const documentHidden = isDocumentHidden(visibilityElement);

  if (documentHidden || !visible) {
    return 0;
  }

  const deltasArray = [
    Math.min(1, (offset.bottom - viewport.top) / offsetHeight), // Top
    Math.min(1, (viewport.bottom - offset.top) / offsetHeight), // Bottom
    Math.min(1, (offset.right - viewport.left) / offsetWidth), // Left
    Math.min(1, (viewport.right - offset.left) / offsetWidth), // Right
  ].sort();

  // returns the smallest value TODO not the most accurate
  return parseInt(deltasArray[0] * 100, 10);
};



// WEBPACK FOOTER //
// ./app/javascript/packs/App/Render/utils/getVisiblePercentage.js